import React, { useState, useMemo, forwardRef, useRef, useImperativeHandle, useEffect } from "react";
import { colors, fonts, glyphs } from "../../Styles";
import { CreateListStyle, ListStyle_Types, CreateNewFieldStyle, CreateNewFieldType, NewFieldStyles, NewFieldTypes, NewInputHandles, NewInputListHandles, ValueSteps } from "../../Definitions";
import { debounce } from "lodash";
import { styled } from "styletron-react";
import { LocaleFarmDate } from "../../Definitions/localeFarmDate";

// #region | Styled Input
interface StyledInputProps {
  fontSize?: string;
  $fontColor?: string;
  $primaryColor?: string;
  secondaryColor?: string;
  width?: string;
  height?: string
  $containerPadding?: string;
  type?: string;
  focus_view?: boolean;
}

export const StyledInput = styled<"input", StyledInputProps>("input", (props) => ({
  height: props.height,
  width: props.width,
  boxSizing: "border-box",
  borderRadius: "10px",
  border: "none",
  backgroundColor: props.$primaryColor,
  outline: "none",
  fontFamily: "Inter Flex",
  fontWeight: "300",
  fontSize: props.focus_view === true ? "14px !important" : "12px",
  lineHeight: "normal",
  margin: "0",
  pointer: "text",
  textTransform: "none",
  padding: props.$containerPadding,
  color: props.$fontColor || colors.greyLightTertiary,
  "::placeholder": {
    color: props.$fontColor || colors.greyPrimary,
    filter: "opacity(0.5)",
    cursor: "text",
  },
  ":-webkit-autofill, :-webkit-autofill:hover, :-webkit-autofill:focus": {
    WebkitTextFillColor: props.$fontColor,
    transition: "background-color 5000s ease-in-out 0s",
    boxShadow: `0 0 0px 1000px ${props.$primaryColor} inset`,
    cursor: "text",
  },
  "::-webkit-inner-spin-button": {
    "-webkit-appearance": "none"
  },
  "::-webkit-outer-spin-button": {
    "-webkit-appearance": "none"
  },
  "input[type=number]": {
    "-moz-appearance": "textfield",
    cursor: "text",
  },
  "input[type=date i]::-webkit-calendar-picker-indicator": {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    width: "auto",
    height: "auto",
    color: "transparent",
    background: "transparent",
    cursor: "pointer"
  },
  ":disabled": {
    color: props.$fontColor + "ab",
    cursor: "not-allowed",
    backgroundColor: props.$primaryColor + "52"
  },
  ...(props.type === "date" && {
    color: "transparent",
    backgroundColor: "transparent",
    zIndex: "1",
    position: "relative"
  })
}));
// #endregion

// #region | New Input

interface newInputProps {
  onChange?: (e: string) => void;
  onBlur?: () => void;
  onKeyEvents?: {
    onKeyDown?: (e: any) => void
    onKeyUp?: (e: any) => void
  }
  placeholder?: string;
  header?: string;
  startEnhancer?: JSX.Element;
  isRequired?: boolean;
  isBlocked?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  fieldOverrides?: {
    fieldStyle?: NewFieldStyles;
    fieldType?: NewFieldTypes;
    fieldWidth?: string;
    disabledNoInput?: boolean;
    focus_view?: boolean;
    showNumberSpinners?: boolean
  };
  valueOverrides?: {
    defaultValue?: string | number;
    valueType?: string;
    valueStep?: ValueSteps;
    minValueNumber?: number;
    maxValueNumber?: number;
  };
}
/**
 * NewInput
 * @param placeholder The placeholder text displayed in the input field.
 * @param defaultValue The initial value of the input field.
 * @param startEnhancer An optional element displayed at the start of the input field.
 * @param isRequired Indicates whether the input field is required.
 * @param isClearable Determines if the input field can be cleared via a button.
 * @param isDisabled Disables the input field.
 * @param fieldOverrides Custom styling and width settings for the input field.
 * @param valueOverrides Default values and settings for the input field.
 * @returns A styled input component with optional start enhancer and clear functionality.
 */
const _NewInput: React.ForwardRefRenderFunction<NewInputHandles, newInputProps> = ({ onChange, onBlur, onKeyEvents, placeholder, header, startEnhancer, isRequired = false, isBlocked, isClearable = false, isDisabled = false, fieldOverrides = {}, valueOverrides = {} }: newInputProps, ref) => {
  const [isDecreasingHovered, setIsDecreasingHovered] = useState<boolean>(false); 
  const [isIncreasingHovered, setIsIncreasingHovered] = useState<boolean>(false); 
  const [, setError] = useState(false)
  const [shouldTriggerSpinnerBlur, setShouldTriggerSpinnerBlur] = useState(false);
  const [isClearHovered, setIsClearHovered] = useState(false);
  const [inputValue, setInputValue] = useState((valueOverrides?.defaultValue || valueOverrides?.defaultValue === 0) ? valueOverrides.defaultValue : "" );
  const _placeholder = placeholder ? placeholder + "..." : "...";
  const _headerShadow = fieldOverrides && fieldOverrides.fieldStyle === NewFieldStyles.greyTertiary ? undefined : { ...fonts.textShadow };
  const { showNumberSpinners = true } = fieldOverrides;

  useEffect(() => {
    if (shouldTriggerSpinnerBlur && onBlur) {
      setShouldTriggerSpinnerBlur(false);
      onBlur()
    }
  },[onBlur, shouldTriggerSpinnerBlur])

  const debouncedSetTriggerBlurState = debounce(() => {
    setShouldTriggerSpinnerBlur(true);
  }, 200)

  useEffect(() => { // TODO Could be removed if loading states were introduced through out the web panel 
    if (valueOverrides?.defaultValue || valueOverrides?.defaultValue === 0) {
      setInputValue(valueOverrides.defaultValue)
    }
  },[valueOverrides?.defaultValue])

  useImperativeHandle(ref, () => ({
    setError(state=true) {
      setError(state)
    },
    value: inputValue,
    setValue: (e: string | number) => { setInputValue(e as string); }
  }));

  const handleClear = () => {
    setInputValue("");
    if (onChange) {onChange(String(""))}
    if (onBlur) {onBlur()}
  };

  // FieldStyle
  const { primaryColor, secondaryColor, fontColor, headerColor } = useMemo(() => {
    var _Style = CreateNewFieldStyle(fieldOverrides?.fieldStyle);
    return _Style;
  }, [fieldOverrides?.fieldStyle]);

  const { containerPaddingTop, containerPaddingRight, containerPaddingBottom, containerPaddingLeft, height } = useMemo(() => {
    var _Style = CreateNewFieldType(fieldOverrides?.fieldType, fieldOverrides?.focus_view);
    return _Style;
  }, [fieldOverrides?.fieldType, fieldOverrides?.focus_view]);

  const _fieldWidth = fieldOverrides && fieldOverrides.fieldWidth !== undefined ? fieldOverrides.fieldWidth : "280px";

  // Date input
  const isDateType = valueOverrides?.valueType === "date";
  const showDatePlaceholder = isDateType && !inputValue;
  const datePlaceholder = "dd/mm/yyyy";

  // #region | Clear Button
  const clearBtn = (
    <svg onClick={handleClear}
      onMouseEnter={() => setIsClearHovered(true)}
      onMouseLeave={() => setIsClearHovered(false)}
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 20 20"
      height="20"
      width="20"
      fill={!isClearHovered ? secondaryColor : colors.greyLightTertiary}
      style={{
        transition: "fill 0.3s ease",
        cursor: "pointer",
        // padding: "8px",
      }}
    >
      <path d="M 14.348 14.849 c -0.469 0.469 -1.229 0.469 -1.697 0 l -2.651 -3.03 l -2.651 
        3.029 c -0.469 0.469 -1.229 0.469 -1.697 0 c -0.469 -0.469 -0.469 -1.229 0 -1.697 l 
        2.758 -3.15 l -2.759 -3.152 c -0.469 -0.469 -0.469 -1.228 0 -1.697 s 1.228 -0.469 
        1.697 0 l 2.652 3.031 l 2.651 -3.031 c 0.469 -0.469 1.228 -0.469 1.697 0 s 0.469 1.229 
        0 1.697 l -2.758 3.152 l 2.758 3.15 c 0.469 0.469 0.469 1.229 0 1.698 Z"
      />
    </svg>
  );
  // #endregion

  // #region | Decreasing and increasing buttons
  const decreasingSpinner = (
    <button 
      onClick={() => {
        const newValue = Number(inputValue) - 1;
        if (valueOverrides?.minValueNumber !== undefined && newValue < valueOverrides.minValueNumber) return;
        setInputValue(newValue);
        if (onChange) {onChange(String(newValue))}
        if (onBlur) {debouncedSetTriggerBlurState()}
      }}
      onMouseEnter={() => setIsDecreasingHovered(true)}
      onMouseLeave={() => setIsDecreasingHovered(false)}
      style={{transition: "fill 0.3s ease", cursor: "pointer", height: "10px", width: "20px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: colors.transparent, border: "none", padding: "0px"}}
    >
      <glyphs.ArrowSmall fillColor={isDecreasingHovered? colors.greyLightTertiary : secondaryColor} rotate={90} size={"10px"}/>
    </button>
  )

  const increasingSpinner = (
    <button 
    onClick={() => {
      const current = Number(inputValue);
      const max = valueOverrides?.maxValueNumber ?? 2147483647;
      if (current >= max) return;
    
      const newValue = current + 1;
      setInputValue(newValue);
      if (onChange) onChange(String(newValue));
      if (onBlur) debouncedSetTriggerBlurState();
    }}
      onMouseEnter={() => setIsIncreasingHovered(true)}
      onMouseLeave={() => setIsIncreasingHovered(false)}
      style={{transition: "fill 0.3s ease", cursor: "pointer", height: "10px", width: "20px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: colors.transparent, border: "none", padding: "0px"}}
    >
      <glyphs.ArrowSmall fillColor={isIncreasingHovered? colors.greyLightTertiary : secondaryColor} rotate={-90} size={"10px"}/>
    </button>
  )

  const spinBtns = ( 
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px"}}>
      {increasingSpinner}
      {decreasingSpinner}
    </div> 
  )
  // #endregion

  // #region | ValueSteps
  let valueStep = 1;

  switch (valueOverrides?.valueStep) {
    case ValueSteps.floats:
      valueStep = 0.1;
      break;
    case ValueSteps.integers:
      default:
      valueStep = 1;
      break;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (valueOverrides?.valueStep === ValueSteps.integers) {
      if (e.key === "." || e.key === "," || e.key === "Decimal") {
        e.preventDefault();
      }
    }
  };
  // #endregion

  return (
    <div style={{ width: _fieldWidth }}>
      {header && <p style={{ ..._headerShadow, ...fonts.header, ...(fieldOverrides?.focus_view ? fonts.fontSize3 : fonts.fontSize4), color: headerColor, padding: "0px 0px 2px 10px", whiteSpace: "nowrap" }}>{header}</p>}
      <div style={{ position: "relative" }}>
        <StyledInput
          value={inputValue}
          onKeyDown={valueOverrides?.valueType === "number" ? handleKeyDown : undefined}
          step={valueStep}
          onChange={e => {
            let newValue = e.target.value;
          
            if (valueOverrides?.valueType === "number") {
              const num = Number(newValue);
          
              if (!isNaN(num)) {
                if (valueOverrides?.maxValueNumber !== undefined && num > valueOverrides.maxValueNumber) {
                  newValue = valueOverrides.maxValueNumber.toString();
                }
                if (valueOverrides?.minValueNumber !== undefined && num < valueOverrides.minValueNumber) {
                  newValue = valueOverrides.minValueNumber.toString();
                }
              }
            }
          
            setInputValue(newValue);
            if (onChange) onChange(newValue);
          }}
          onBlur={onBlur}
          onKeyUpCapture={(e) => onKeyEvents?.onKeyUp?.(e)}
          onKeyDownCapture={(e) => onKeyEvents?.onKeyDown?.(e)}
          focus_view={fieldOverrides?.focus_view}
          placeholder={isDateType ? datePlaceholder : _placeholder}
          disabled={isDisabled}
          defaultValue={
            valueOverrides?.valueType === "date" ? (() => {
              const value = valueOverrides?.defaultValue;
              if (typeof value === "string") {
                const ts = Date.parse(value);
                return isNaN(ts) ? "" : String(ts);
              }
              return "";
            })() : valueOverrides?.defaultValue
          }
          type={valueOverrides?.valueType}
          autoComplete="new-password"
          min={valueOverrides?.minValueNumber}
          max={valueOverrides?.maxValueNumber}
          $fontColor={fontColor}
          $primaryColor={(fieldOverrides&&fieldOverrides.fieldStyle === NewFieldStyles.transparent) ? colors.transparent : primaryColor}
          width={_fieldWidth}
          height={`${height}px`}
          $containerPadding={
            startEnhancer ? "6px 8px 6px 38px"
              // : `${containerPaddingTop + 4}px ${containerPaddingRight + 2}px ${containerPaddingBottom + 4}px ${containerPaddingLeft + 2}px`
              : `${containerPaddingTop}px ${containerPaddingRight}px ${containerPaddingBottom}px ${containerPaddingLeft}px`

          }
        />

        {/* DATE FIELD */}
        {valueOverrides?.valueType === "date" ? (
          <div style={{ 
            position: "absolute", 
            zIndex: "0", 
            top: "0", 
            left: "0", 
            backgroundColor: primaryColor, 
            borderRadius: "10px",
            width: _fieldWidth, 
            height: `${height}px`, 
            boxSizing: "border-box",
            padding: startEnhancer ? "6px 8px 6px 38px" : `${containerPaddingTop + 4}px ${containerPaddingRight + 2}px ${containerPaddingBottom + 4}px ${containerPaddingLeft + 2}px`,
            display: "flex", 
            flexDirection: "row", 
            alignItems: "center", 
            justifyContent: "left",
          }}>
            <p style={{ color: fontColor, ...(fieldOverrides?.focus_view ? fonts.fontSize3 : fonts.fontSize4), ...fonts.paragraph, filter: showDatePlaceholder ? "opacity(0.5)" : "none"}}>{showDatePlaceholder ? "dd/mm/yyyy" : new LocaleFarmDate(inputValue).toDateForDisplay() }</p>
          </div>
        ):<></>}
        
        {/* NUMBER FIELD (arrows) */}
        {!isDisabled && (isRequired || isBlocked || showNumberSpinners) ? 
        <div style={{ position: "absolute", zIndex: "99",  height: "22px", top: "50%", transform: "translateY(-50%)", right: "0px", display: "grid", gridAutoFlow: "column", gridAutoColumns: "1fr", padding: "4px 8px", alignItems: "center", cursor: "text", }}>
          {isRequired && inputValue === "" ? <glyphs.Error fillColor={colors.systemRed} /> : <></>}
          {isBlocked ? <glyphs.Error fillColor={colors.systemRed} /> : <></>}
          {valueOverrides?.valueType === "number" && showNumberSpinners ? <>{spinBtns}</> : <></>}
          {!isDisabled && isClearable && inputValue !== "" ? <>{clearBtn}</> : <></>}
        </div> : undefined }

        {/* STARTENHANCER */}
        {startEnhancer ? (
          <div style={{ position: "absolute", height: "20px", top: "50%", transform: "translateY(-50%)", left: "8px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
            {startEnhancer}
          </div>
        ):<></>}
      </div>
    </div>
  );
};
const NewInput = forwardRef(_NewInput);
export default NewInput;
// #endregion

// #region | New Input List
interface newInputListProps {
  onChanges?: ((e: string) => void)[];
  placeholders?: string[];
  headers?: string[];
  startEnhancers?: JSX.Element[];
  isRequired?: boolean[];
  isBlocked?: boolean[];
  isClearable?: boolean;
  isDisabled?: boolean[];
  fieldOverrides?: {
    fieldStyle?: NewFieldStyles;
    fieldType?: NewFieldTypes;
    fieldWidth?: string;
    focus_view?: boolean;
  };
  valueOverrides?: {
    defaultValues?: (string | number)[];
    valueTypes?: string[];
    minValueNumbers?: (number | undefined)[];
    maxValueNumbers?: (number | undefined)[];
  };
  listOverrides?: {
    listGap?: string;
    listStyle?: ListStyle_Types;
  };
}
/**
 * NewInputList
 * @param placeholders An array of placeholder texts for each input field.
 * @param defaultValues An array of default values for each input field.
 * @param startEnhancers An array of elements displayed at the start of each input field.
 * @param isRequired An array indicating whether each input field is required.
 * @param isClearable Determines if each input field can be cleared via a button.
 * @param isDisabled An array indicating whether each input field is disabled.
 * @param fieldOverrides Custom styling and width settings for all input fields.
 * @param valueOverrides Default values and settings for each input field.
 * @param listOverrides Custom styling and layout settings for the list container.
 * @returns A list of styled input components with optional start enhancers and clear functionality.
 */
const _NewInputList: React.ForwardRefRenderFunction<NewInputListHandles, newInputListProps> = ({ onChanges, placeholders, headers, startEnhancers, isRequired = [false], isBlocked, isClearable = false, isDisabled = [false], fieldOverrides, valueOverrides, listOverrides }: newInputListProps, ref) => {
  const inputRefs = useRef<NewInputHandles[]>([]);
  const _listGap = listOverrides && listOverrides.listGap ? { gap: listOverrides.listGap } : { gap: "5px" };
  const { listDirection } = useMemo(() => CreateListStyle(listOverrides && listOverrides.listStyle), [listOverrides]);

  useImperativeHandle(ref, () => ({
    setError(index=0,state) { 
      if (inputRefs.current && inputRefs.current[index]) inputRefs.current[index].setError(state)
    },
    value(index = 0): string | number | undefined {
      if (inputRefs.current && inputRefs.current[index]) return inputRefs.current[index]?.value;
      return undefined;
    },
    refs: inputRefs
  }));

  const Item_List = useMemo(() => {
    return placeholders?.map((_placeholders, index) => {
      const _onChanges = onChanges && onChanges[index] ? onChanges[index] : undefined;
      const _headers = headers && headers[index] ? headers[index] : "";
      const _startEnhancers = startEnhancers && startEnhancers[index] ? startEnhancers[index] : undefined;
      const _isRequired = isRequired && isRequired[index] ? isRequired[index] : undefined;
      const _isBlocked = isBlocked !== undefined && isBlocked[index] ? isBlocked[index] : undefined;
      const _isDisabled = isDisabled && isDisabled[index] ? isDisabled[index] : undefined;
      const _fieldOverrides = fieldOverrides || {};
      // const _defaultValues = valueOverrides && valueOverrides.defaultValues && valueOverrides.defaultValues[index] ? valueOverrides.defaultValues[index] : undefined;
      // const _valueTypes = valueOverrides && valueOverrides.valueTypes && valueOverrides.valueTypes[index] ? valueOverrides.valueTypes[index] : undefined;
      // const _minValueNumbers = valueOverrides && valueOverrides.minValueNumbers && valueOverrides.minValueNumbers[index] ? valueOverrides.minValueNumbers[index] : undefined;
      // const _maxValueNumbers = valueOverrides && valueOverrides.maxValueNumbers && valueOverrides.maxValueNumbers[index] ? valueOverrides.maxValueNumbers[index] : undefined;
      const _minValueNumbers = valueOverrides?.minValueNumbers?.[index] !== undefined ? valueOverrides.minValueNumbers[index] : undefined;
      const _maxValueNumbers = valueOverrides?.maxValueNumbers?.[index] !== undefined ? valueOverrides.maxValueNumbers[index] : undefined;
      const _defaultValues = valueOverrides?.defaultValues?.[index] !== undefined ? valueOverrides.defaultValues[index] : undefined;
      const _valueTypes = valueOverrides?.valueTypes?.[index] !== undefined ? valueOverrides.valueTypes[index] : undefined;
      
      var view = [
        <NewInput
          key={"List_Input_" + index}
          ref={(el) => el ? inputRefs.current[index] = el : null}
          onChange={_onChanges}
          placeholder={_placeholders}
          header={_headers}
          startEnhancer={_startEnhancers}
          isRequired={_isRequired}
          isBlocked={_isBlocked}
          isClearable={isClearable}
          isDisabled={_isDisabled}
          fieldOverrides={_fieldOverrides}
          valueOverrides={{
            defaultValue: _defaultValues,
            valueType: _valueTypes,
            minValueNumber: _minValueNumbers,
            maxValueNumber: _maxValueNumbers
          }}
        />
      ];

      return view;
    });
  }, [placeholders, onChanges, headers, startEnhancers, isRequired, isBlocked, isDisabled, fieldOverrides, valueOverrides, isClearable]);

  return (
    <div style={{ ...listDirection as React.CSSProperties, ..._listGap }}>
      {Item_List}
    </div>
  );
}
export const NewInputList = forwardRef(_NewInputList);
// #endregion